import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import DataTable from "@/components/DataTable";
import EmptyData from "@/components/EmptyData";
import Kpi, {KpiRow} from "@/components/Kpi";
import StatusBadge from "@/components/StatusBadge";
import VLFTextField from "@/components/Input/TextField";
import AttachmentsContainer from "@/components/AttachmentsContainer";
import VLFModal from "@/components/Modal";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

import {
	MedicalAPI,
	injuryDaysOut,
	isInjuryActiveAt,
	loadMedicalRosterMemberships,
} from "@/api/medical";
import {AttachmentAPI} from "@/api/attachment";

import {
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	dateFormat,
} from "@/utils/constants";
import {getAttachment} from "@/utils/functions";
import {AREAS, canWrite} from "@/utils/access";

import "./index.scss";

const ANAMNESIS = [
	{key: "physiologicalAnamnesis", label: "Anamnesi fisiologica"},
	{key: "pathologicalAnamnesis", label: "Anamnesi patologica"},
	{key: "traumatologicalAnamnesis", label: "Anamnesi traumatologica"},
	{key: "surgicalHistory", label: "Storia chirurgica"},
];

const formatDate = (value) => (value ? moment(value).format(dateFormat) : "");

const playerName = (record) =>
	[record?.playerData?.firstName, record?.playerData?.lastName]
		.filter(Boolean)
		.join(" ");

// Cartella del giocatore: pagina propria (/medical/records/:id) o tab della scheda giocatore (playerId)
function MedicalRecordPage(props) {
	const {handleNotification, onlyDetails = false, playerId} = props;

	const {id: recordId} = useParams();
	const navigate = useNavigate();
	const user = useSelector((state) => state?.user);
	const seasonId = useSelector((state) => state?.season?.id);
	const writable = canWrite(user, AREAS.AREA_MEDICA);

	const [loading, setLoading] = useState(false);
	// undefined = in caricamento, null = il giocatore non ha ancora la cartella
	const [record, setRecord] = useState();
	const [fields, setFields] = useState({});
	const [attachments, setAttachments] = useState([]);
	const [currentRosters, setCurrentRosters] = useState([]);
	const [toDelete, setToDelete] = useState(null);

	const loadAttachments = async (id) => {
		try {
			const response = await AttachmentAPI.list("medical_record", id);
			setAttachments(response || []);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		}
	};

	const showRecord = (data) => {
		setRecord(data);
		setFields(data || {});
		if (data?.id) loadAttachments(data.id);
	};

	const getRecord = async () => {
		setLoading(true);
		try {
			if (onlyDetails) {
				const list = await MedicalAPI.recordsList({playerId});
				const found = (list || []).find((r) => r.playerId === playerId);
				showRecord(found ? await MedicalAPI.recordGetById(found.id) : null);
			} else {
				showRecord(await MedicalAPI.recordGetById(recordId));
			}
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		getRecord();
	}, [recordId, playerId]);

	useEffect(() => {
		if (onlyDetails || !record?.playerId) return;
		let active = true;
		loadMedicalRosterMemberships(seasonId)
			.then((memberships) => {
				if (active) setCurrentRosters(memberships.get(record.playerId) || []);
			})
			.catch(() => {
				if (active) handleNotification(ERROR_OPERATION, "error");
			});
		return () => {
			active = false;
		};
	}, [seasonId, record?.playerId]);

	const createRecord = async () => {
		setLoading(true);
		try {
			const created = await MedicalAPI.recordCreate({playerId, notes: ""});
			showRecord(await MedicalAPI.recordGetById(created.id));
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const saveAnamnesis = async () => {
		setLoading(true);
		try {
			const {injuriesIllness, playerData, ...body} = fields;
			await MedicalAPI.recordUpdate(record.id, body);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			getRecord();
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		}
	};

	const confirmDelete = async () => {
		setLoading(true);
		try {
			if (toDelete.type === "record") {
				await MedicalAPI.recordDelete(record.id);
				handleNotification(SUCCESSFUL_OPERATION, "success");
				navigate("/medical/records");
				return;
			}
			await AttachmentAPI.delete(toDelete.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			loadAttachments(record.id);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setToDelete(null);
			setLoading(false);
		}
	};

	const handleDownload = async (id) => {
		setLoading(true);
		try {
			await getAttachment(id, attachments);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	if (record === null) {
		return (
			<EmptyData
				action={
					writable && (
						<MainActions
							actions={[
								{
									icon: <AddIcon />,
									label: "Apri cartella",
									onClick: createRecord,
								},
							]}
						/>
					)
				}
			>
				Il giocatore non ha ancora una cartella medica.
			</EmptyData>
		);
	}

	const items = record?.injuriesIllness || [];
	const injuries = items.filter((r) => !r.recordType);
	const illnesses = items.filter((r) => r.recordType);
	const injured = injuries.some((inj) => isInjuryActiveAt(inj, moment()));
	const totalDaysOut = items.reduce(
		(sum, i) => sum + (injuryDaysOut(i) || 0),
		0,
	);
	const recordPath = "/medical/records/" + record?.id;

	const columns = (dischargeKey, dischargeLabel) => [
		{
			key: "startDate",
			label: "Inizio",
			render: (row) => formatDate(row.startDate),
			sortValue: (row) => row.startDate,
		},
		{key: "diagnosis", label: "Diagnosi", sortable: true},
		...(dischargeKey === "sportsDischargeDate"
			? [{key: "bodyPart", label: "Parte del corpo", sortable: true}]
			: []),
		{
			key: dischargeKey,
			label: dischargeLabel,
			render: (row) =>
				row[dischargeKey] ? (
					formatDate(row[dischargeKey])
				) : (
					<StatusBadge tone="warning" label="In corso" />
				),
			sortValue: (row) => row[dischargeKey],
		},
		{
			key: "daysOut",
			label: "Giorni fuori",
			align: "end",
			render: (row) => injuryDaysOut(row) ?? "",
			sortValue: injuryDaysOut,
		},
	];

	const section = (
		title,
		rows,
		dischargeKey,
		dischargeLabel,
		newLabel,
		kind,
	) => (
		<VLFCard
			title={title}
			content={
				<>
					{writable && (
						<div className="main-actions-bar">
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: newLabel,
										onClick: () =>
											navigate(recordPath + "/injuries/new?tipo=" + kind),
									},
								]}
							/>
						</div>
					)}
					<DataTable
						columns={columns(dischargeKey, dischargeLabel)}
						rows={rows}
						onRowClick={(row) => navigate(recordPath + "/injuries/" + row.id)}
						empty={
							"Nessun" + (kind === "malattia" ? "a malattia" : " infortunio")
						}
					/>
				</>
			}
		/>
	);

	const details = record && (
		<div className="medical-record">
			<VLFCard
				title="Anamnesi"
				content={
					<>
						{writable && onlyDetails && (
							<div className="main-actions-bar">
								<MainActions
									actions={[
										{
											icon: <SaveIcon />,
											label: "Salva anamnesi",
											onClick: saveAnamnesis,
										},
									]}
								/>
							</div>
						)}
						<div className="row">
							{ANAMNESIS.map((a) => (
								<div key={a.key} className="col-12 col-lg-6 padding-small">
									<VLFTextField
										value={fields?.[a.key] || ""}
										onChange={(e) =>
											setFields({...fields, [a.key]: e.target.value})
										}
										label={a.label}
										readOnly={!writable}
										multiline
										minRows={3}
										maxRows={6}
									/>
								</div>
							))}
						</div>
					</>
				}
			/>
			{section(
				"Infortuni",
				injuries,
				"sportsDischargeDate",
				"Rientro in campo",
				"Nuovo infortunio",
				"infortunio",
			)}
			{section(
				"Malattie",
				illnesses,
				"medicalDischargeDate",
				"Guarigione",
				"Nuova malattia",
				"malattia",
			)}
			<VLFCard
				title="Allegati"
				content={
					<AttachmentsContainer
						entityId={record.id}
						entityType="medical_record"
						attachments={attachments}
						editable={writable}
						handleAdd={() => loadAttachments(record.id)}
						handleDownload={handleDownload}
						handleDelete={(id) => setToDelete({type: "attachment", id})}
						handleNotification={handleNotification}
					/>
				}
			/>
		</div>
	);

	const deleteModal = (
		<VLFModal
			open={!!toDelete}
			title="Eliminazione"
			content={
				<div className="mt-2">
					{toDelete?.type === "record"
						? "Eliminare la cartella medica di " + playerName(record) + "?"
						: "Eliminare l'allegato?"}
				</div>
			}
			confirm={confirmDelete}
			close={() => setToDelete(null)}
			confirmLabel="Elimina"
		/>
	);

	if (onlyDetails) {
		return (
			<>
				{details}
				{deleteModal}
			</>
		);
	}

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate("/medical/records"),
			color: "info",
		},
		{icon: <SaveIcon />, title: "Salva anamnesi", onClick: saveAnamnesis},
		{
			icon: <DeleteIcon />,
			title: "Elimina cartella",
			onClick: () => setToDelete({type: "record"}),
			color: "error",
			variant: "outlined",
		},
	];

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[{label: "Cartelle", path: "/medical/records"}, {label: "/"}]}
				current={playerName(record)}
				actions={<MainActions actions={mainActions} />}
			/>
			{record && (
				<>
					<div className="medical-record__head">
						<div className="medical-record__name">{playerName(record)}</div>
						{currentRosters.length > 0 && (
							<div className="medical-record__rosters">
								{currentRosters.map((r) => r.name).join(", ")}
							</div>
						)}
						<StatusBadge
							tone={injured ? "critical" : "good"}
							label={injured ? "Infortunato" : "Disponibile"}
						/>
					</div>
					<KpiRow>
						<Kpi label="Infortuni" value={injuries.length} />
						<Kpi label="Malattie" value={illnesses.length} />
						<Kpi label="Giorni fuori" value={totalDaysOut} />
					</KpiRow>
				</>
			)}
			{details}
			{deleteModal}
		</Page>
	);
}

export default MedicalRecordPage;
